/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import {hideWhitespaceAtom} from './jotai';
import {GearIcon} from '@primer/octicons-react';
import {ActionList, ActionMenu, IconButton} from '@primer/react';
import {useAtom} from 'jotai';
import {useCallback} from 'react';

/**
 * Gear icon dropdown for diff display settings, inspired by GitHub's
 * diff settings menu. Appears next to the version/commit selectors
 * in the PR header.
 */
export default function DiffSettingsDropdown(): React.ReactElement {
  const [hideWhitespace, setHideWhitespace] = useAtom(hideWhitespaceAtom);

  const toggleHideWhitespace = useCallback(() => {
    setHideWhitespace(prev => !prev);
  }, [setHideWhitespace]);

  return (
    <ActionMenu>
      <ActionMenu.Anchor>
        <IconButton
          icon={GearIcon}
          aria-label="Diff settings"
          size="medium"
        />
      </ActionMenu.Anchor>
      <ActionMenu.Overlay>
        <ActionList>
          <ActionList.Item onSelect={toggleHideWhitespace}>
            {hideWhitespace && (
              <ActionList.LeadingVisual>
                <CheckIcon />
              </ActionList.LeadingVisual>
            )}
            Hide whitespace
          </ActionList.Item>
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  );
}

/** Simple check mark icon matching GitHub's style. */
function CheckIcon(): React.ReactElement {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
      <path d="M13.78 4.22a.75.75 0 0 1 0 1.06l-7.25 7.25a.75.75 0 0 1-1.06 0L2.22 9.28a.751.751 0 0 1 .018-1.042.751.751 0 0 1 1.042-.018L6 10.94l6.72-6.72a.75.75 0 0 1 1.06 0Z" />
    </svg>
  );
}
